import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {RepoIcon, RepoLockedIcon, TriangleDownIcon} from '@primer/octicons-react'
import type {AnchorSide} from '@primer/behaviors'
import {Button, IconButton, Truncate, useResponsiveValue, type ButtonProps, type OverlayProps} from '@primer/react'
import {debounce} from '@github/mini-throttle'
import {ItemPicker} from '@github-ui/item-picker-base/ItemPicker'

import styles from './ReposSelector.module.css'

// A simple repository that only requires a `name` field but can contain additional
// fields to enrich rendering
interface NamedRepository {
  name: string
  enabled?: boolean
  private?: boolean
}

// Internal repo used to represent "All repositories"
interface AllRepo extends NamedRepository {
  _all: true
}

interface SharedProps {
  selectAllOption: boolean
  additionalButtonProps?: Partial<ButtonProps>
  buttonText?: string
  width?: OverlayProps['width']
  // Optional max width in pixels for button label truncation
  truncateMaxWidth?: number
  /** Height passed to the underlying overlay (defaults to ItemPicker default if omitted). */
  height?: OverlayProps['height']
  /** When true, enable fullscreen behavior on narrow viewports (maps to ItemPicker responsiveOnNarrow). */
  fullscreenOnNarrow?: boolean
  loadingElement?: React.ReactNode
  /** When true, enable live selection mode where changes are applied immediately. Only applicable for multiple selection variant. */
  liveSelection?: boolean
  /** Optional callback invoked when the overlay is opened */
  onOpen?: () => void
  /** Responsive styling variant: 'full' always shows text, 'icon-on-mobile' hides text on small viewports */
  responsiveStyle?: 'full' | 'icon-on-mobile'
  /** When true, always show only the icon button regardless of viewport size */
  iconOnly?: boolean
  /** The preferred side for the overlay to open relative to the anchor. */
  anchorSide?: AnchorSide
  /** The direction for the tooltip to appear when using icon-only mode */
  tooltipDirection?: 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'
}

export interface SingleSelectProps<T> extends SharedProps {
  currentSelection: T | undefined
  repositoryLoader: (q: string, defaultOwner?: string) => Promise<T[]>
  onSelect: (repository: T | undefined) => void
  selectionVariant: 'single'
  /** Optional organization login to scope repository search */
  defaultOwner?: string
  /** Optional callback invoked when repositories are loaded */
  onLoad?: (repositories: T[]) => void
}

export interface MultiSelectProps<T> extends SharedProps {
  currentSelection: T[] | undefined
  repositoryLoader: (q: string, defaultOwner?: string) => Promise<T[]>
  onSelect: (repository: T[] | undefined) => void
  selectionVariant: 'multiple'
  selectAllOption: false
  additionalButtonProps?: Partial<ButtonProps>
  /** Optional organization login to scope repository search */
  defaultOwner?: string
  /** Optional callback invoked when repositories are loaded */
  onLoad?: (repositories: T[]) => void
}

export function ReposSelector<T extends NamedRepository>({
  currentSelection,
  repositoryLoader,
  onSelect,
  selectionVariant,
  selectAllOption,
  additionalButtonProps,
  buttonText: _buttonText = '',
  width,
  truncateMaxWidth,
  height,
  fullscreenOnNarrow,
  defaultOwner,
  onLoad,
  loadingElement,
  liveSelection = false,
  onOpen,
  responsiveStyle = 'full',
  iconOnly = false,
  anchorSide,
  tooltipDirection,
}: SingleSelectProps<T> | MultiSelectProps<T>) {
  const [filterText, setFilterText] = useState('')
  const [filteredRepos, setFilteredRepos] = useState<T[]>([])
  const [isLoadingRepos, setIsLoadingRepos] = useState<boolean>(true)
  const [initialLoadComplete, setInitialLoadComplete] = useState(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceRepos = useCallback(
    debounce(async (newFilter: string) => {
      try {
        const response = await repositoryLoader(newFilter, defaultOwner)
        setFilteredRepos(response)
        onLoad?.(response)
      } catch {
        // suppress repo loading errors
      }
      setIsLoadingRepos(false)
      setInitialLoadComplete(true)
    }, 200),
    [repositoryLoader, defaultOwner],
  )

  useEffect(() => {
    setIsLoadingRepos(true)
    debounceRepos(filterText)
  }, [filterText, debounceRepos, setIsLoadingRepos])

  const selectRepos = useCallback(
    (repos: Array<T | AllRepo>) => {
      if (selectionVariant === 'single') {
        if (!repos[0] || (repos[0] as AllRepo)._all) {
          onSelect(undefined)
        } else {
          onSelect(repos[0] as T)
        }
      } else {
        onSelect(repos as T[])
      }
    },
    [selectionVariant, onSelect],
  )

  const selected = useMemo(() => {
    return selectionVariant === 'multiple' ? currentSelection || [] : currentSelection ? [currentSelection] : []
  }, [selectionVariant, currentSelection])

  const convertToItemProps = useCallback(
    (repo: T | AllRepo) => {
      // Character limits based on overlay width:
      // small (256px) -> 24 chars, medium (320px) -> 35 chars, large (480px) -> 60 chars
      const truncationCharLimit = width === 'medium' ? 35 : width === 'large' ? 60 : 24
      let truncatedRepoName = undefined

      if (repo.name.length > truncationCharLimit) {
        truncatedRepoName = `${repo.name.substring(0, truncationCharLimit)}...`
      }

      if ((repo as AllRepo)._all) {
        return {
          id: repo.name,
          text: 'All repositories',
          source: repo,
          groupId: 'all',
        }
      }
      return {
        id: repo.name,
        text: truncatedRepoName || repo.name,
        source: repo,
        groupId: 'select',
        disabled: repo.enabled === false,
        leadingVisual: () => {
          if (repo.private) {
            return <RepoLockedIcon size={16} />
          } else {
            return <RepoIcon size={16} />
          }
        },
      }
    },
    [width],
  )

  const getItemKey = useCallback((repo: T | AllRepo) => repo.name, [])

  const items = useMemo(() => {
    const memoItems: Array<T | AllRepo> = []
    memoItems.push(...filteredRepos)
    if (selectAllOption) {
      memoItems.push({name: '$all$', _all: true} as AllRepo)
    }
    return memoItems
  }, [filteredRepos, selectAllOption])

  let buttonText = _buttonText
  let buttonIcon = RepoIcon
  if (!buttonText) {
    if (selectionVariant === 'multiple') {
      buttonText =
        selected.length > 0 ? `${selected.length} repositor${selected.length > 1 ? 'ies' : 'y'}` : 'Select repositories'
    } else {
      if (selected.length > 0 && selected[0]) {
        buttonText = selected[0].name
        buttonIcon = selected[0].private ? RepoLockedIcon : RepoIcon
      } else if (selectAllOption) {
        buttonText = 'All repositories'
      } else {
        buttonText = 'Select a repository'
      }
    }
  }

  const isNarrow = useResponsiveValue(
    responsiveStyle === 'icon-on-mobile' ? {narrow: true} : {narrow: false},
    false,
  ) as boolean
  // Show icon only when iconOnly prop is true or on narrow screens with icon-on-mobile style
  const showIconOnly = iconOnly || isNarrow

  return loadingElement && isLoadingRepos && !currentSelection && !initialLoadComplete ? (
    loadingElement
  ) : (
    <ItemPicker
      renderAnchor={({'aria-labelledby': ariaLabelledBy, ...anchorProps}) => {
        const {'aria-label': _ariaLabel, ...iconAnchorProps} = anchorProps
        const iconButtonAriaLabel = additionalButtonProps?.['aria-label'] ?? _ariaLabel ?? buttonText
        return showIconOnly ? (
          <IconButton
            variant="invisible"
            {...(ariaLabelledBy ? {'aria-labelledby': ariaLabelledBy} : {'aria-label': iconButtonAriaLabel})}
            icon={(additionalButtonProps?.leadingVisual as React.ElementType) || buttonIcon}
            {...iconAnchorProps}
            className={`${styles.invisibleWithBorder}${selected.length ? ` ${styles.selected}` : ''}`}
            tooltipDirection={tooltipDirection}
          />
        ) : (
          <Button
            variant={responsiveStyle === 'icon-on-mobile' ? 'invisible' : undefined}
            leadingVisual={buttonIcon}
            trailingAction={TriangleDownIcon}
            aria-labelledby={ariaLabelledBy}
            {...anchorProps}
            {...additionalButtonProps}
            className={`${additionalButtonProps?.className}${
              responsiveStyle === 'icon-on-mobile' ? ` ${styles.invisibleWithBorder}` : ''
            }`}
          >
            <Truncate title={buttonText} maxWidth={truncateMaxWidth ?? 195}>
              {buttonText}
            </Truncate>
          </Button>
        )
      }}
      placeholderText="Search"
      items={items}
      loading={isLoadingRepos}
      groups={selectAllOption ? [{groupId: 'all'}, {groupId: 'select'}] : []}
      selectionVariant={selectionVariant}
      initialSelectedItems={selected}
      onSelectionChange={selectRepos as (v: unknown[]) => void}
      onLiveSelectionChange={
        selectionVariant === 'multiple' && liveSelection ? (selectRepos as (v: unknown[]) => void) : undefined
      }
      filterItems={setFilterText}
      getItemKey={getItemKey}
      convertToItemProps={convertToItemProps}
      title="Select a repository"
      width={width}
      height={height}
      responsiveOnNarrow={fullscreenOnNarrow}
      onOpen={onOpen}
      anchorSide={anchorSide}
    />
  )
}

export function simpleRepoLoader<T extends NamedRepository>(repos: T[]): (q: string, org?: string) => Promise<T[]> {
  return async (q: string, _org?: string) => {
    const trimmedFilterText = q.trim().toLowerCase()

    if (!trimmedFilterText) {
      return repos
    }

    return repos.filter(
      repo =>
        repo.name.toLowerCase().includes(trimmedFilterText) || repo.name.toLowerCase().includes(trimmedFilterText),
    )
  }
}
